import {
  type Accessor,
  type Component,
  type ContextProviderComponent,
  createContext,
  createMemo,
  untrack,
} from "solid-js";
import { initializedContext } from "../../../utils/initializedUtil";
import { MAX_DISPLAYED_BANNERS } from "../../../constants";
import { useLocalUserSettings, useLocalUserSettingsDispatch } from "../../../state/localUserSettings.tsx";

export type Banner = {
  id: string;
  priority: number;
  dismissable?: boolean;
  useShouldDisplay: () => Accessor<boolean>;
  Component: BannerComponent;
};

export type BannerComponent = Component<{ onDismiss?: () => void }>;

type BannersContextValue = {
  visibleBanners: Accessor<Banner[]>;
  dismissBanner: (id: string) => void;
};

const BannersContext = createContext<BannersContextValue>();

type BannersProviderProps = {
  banners: Banner[];
};

export const BannersProvider: ContextProviderComponent<BannersProviderProps> = (props) => {
  const settings = useLocalUserSettings();
  const setLocalUserSettings = useLocalUserSettingsDispatch();

  const dismissBanner = (id: string) =>
    setLocalUserSettings("dismissedBanners", (prev) => (prev?.includes(id) ? prev : [...(prev ?? []), id]));

  const visibility = untrack(() =>
    props.value.banners.map((banner) => ({ banner, shouldDisplay: banner.useShouldDisplay() })),
  );

  const visibleBanners = createMemo(() =>
    visibility
      .filter(({ banner, shouldDisplay }) => shouldDisplay() && !settings.dismissedBanners?.includes(banner.id))
      .map(({ banner }) => banner)
      .sort((a, b) => b.priority - a.priority)
      .slice(0, MAX_DISPLAYED_BANNERS),
  );

  return <BannersContext.Provider value={{ visibleBanners, dismissBanner }}>{props.children}</BannersContext.Provider>;
};

export const useVisibleBanners = () =>
  initializedContext(BannersContext, "useVisibleBanners", "BannersProvider").visibleBanners;

export const useDismissBanner = () =>
  initializedContext(BannersContext, "useDismissBanner", "BannersProvider").dismissBanner;
